import React, { useMemo } from "react";
import { FiChevronRight, FiAlertCircle, FiInbox } from "react-icons/fi";
import { Group, GroupLabel, Row, IconTile, Pill, Empty, Skeleton } from "../styles";
import { dayKey, dayLabel, timeLabel, describe, eventMeta, fmtDuration, IOS } from "../labels";

/**
 * Linha do tempo agrupada por dia, no estilo da central de notificações do iOS.
 * - showUser / showProject controlam o que aparece no subtítulo (no detalhe de
 *   um usuário não faz sentido repetir o nome dele em toda linha).
 */
export default function Feed({ items, loading, onSelect, showUser = true, showProject = true, stickyTop, emptyText }) {
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((e) => {
      const k = dayKey(e.timestamp);
      if (!map.has(k)) map.set(k, { key: k, label: dayLabel(e.timestamp), items: [] });
      map.get(k).items.push(e);
    });
    return [...map.values()];
  }, [items]);

  if (loading) {
    return (
      <Group>
        {[0, 1, 2, 3, 4].map((i) => (
          <Row key={i} $inset="59px">
            <Skeleton $w="30px" $h={30} $r={8} />
            <div className="main">
              <Skeleton $w={`${60 - i * 6}%`} $h={12} />
              <div style={{ height: 6 }} />
              <Skeleton $w={`${40 - i * 4}%`} $h={10} />
            </div>
          </Row>
        ))}
      </Group>
    );
  }

  if (items.length === 0) {
    return (
      <Group>
        <Empty>
          <div className="e-icon"><FiInbox /></div>
          <div className="e-title">Nenhuma atividade</div>
          <div className="e-sub">{emptyText || "Nada registrado com esses filtros no período."}</div>
        </Empty>
      </Group>
    );
  }

  return groups.map((g, gi) => (
    <div key={g.key}>
      <GroupLabel $first={gi === 0} $sticky={stickyTop !== undefined} $stickyTop={stickyTop}>
        <span>{g.label}</span>
        <span>{g.items.length} {g.items.length === 1 ? "evento" : "eventos"}</span>
      </GroupLabel>
      <Group>
        {g.items.map((e) => {
          const meta = eventMeta(e);
          const Icon = meta.icon;
          const sub = [showUser && (e.userName || e.userEmail), showProject && e.projectName]
            .filter(Boolean)
            .join(" · ");
          const duration = e.type !== "page_view" ? fmtDuration(e.durationMs) : null;

          return (
            <Row key={e.id} $clickable $inset="59px" onClick={() => onSelect?.(e)}>
              <IconTile $color={e.status === "error" ? IOS.red : meta.color}>
                {e.status === "error" ? <FiAlertCircle /> : <Icon />}
              </IconTile>
              <div className="main">
                <div className="title">
                  {describe(e)}
                  {e.realUserId && <Pill $color={IOS.orange}>simulado</Pill>}
                </div>
                <div className="subtitle">
                  {e.status === "error" && e.errorMessage ? (
                    <span style={{ color: IOS.red }}>{e.errorMessage}</span>
                  ) : (
                    sub || meta.label
                  )}
                </div>
              </div>
              <div className="trailing">
                {duration && <span>{duration}</span>}
                <span>{timeLabel(e.timestamp)}</span>
                <FiChevronRight className="chevron" />
              </div>
            </Row>
          );
        })}
      </Group>
    </div>
  ));
}
